import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './WikiSidebar.css';
import { getPageTreeForSpace } from '../../../services/wikiServices/WikiSpaceService';
import { useAuth } from '../../../firebase/AuthProvider';
import { PageNode, Space } from '../../../interfaces/wiki/Wiki';

interface PageTreeItemProps {
  node: PageNode;
  spaceId: string;
  level: number;
}

const PageTreeItem: React.FC<PageTreeItemProps> = ({ node, spaceId, level }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  const toggleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <li className="page-tree-item">
      <div style={{ paddingLeft: `${level * 15}px` }} className="page-item-content">
        {hasChildren && (
          <FontAwesomeIcon
            icon={isOpen ? faChevronDown : faChevronRight}
            className="tree-toggle-icon"
            onClick={toggleOpen}
          />
        )}
        <NavLink
          to={`/wiki/spaces/${spaceId}/${node.id}`}
          className="sidebar-link"
        >
          {node.title}
        </NavLink>
      </div>
      {hasChildren && isOpen && (
        <ul className="page-tree-children">
          {node.children.map((child) => (
            <PageTreeItem key={child.id} node={child} spaceId={spaceId} level={level + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};

const WikiSidebar: React.FC = () => {
  const { spaceId } = useParams<{ spaceId: string; pageId?: string }>();
  const location = useLocation();
  const state = location.state as { space: Space }; // Access the state passed from the Link
  const spaceName = state?.space?.name;

  const [pageTree, setPageTree] = useState<PageNode[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { currentUser } = useAuth();

  const fetchPageTree = useCallback(async () => {
    if (currentUser && spaceId) {
      setLoading(true);
      const token = await currentUser.getIdToken();
      try {
        const results = await getPageTreeForSpace(token, spaceId);
        if (results) {
          setPageTree(results);
        }
      } catch (error) {
        console.error(`Failed to fetch page tree for space ${spaceId}:`, error);
      } finally {
        setLoading(false);
      }
    }
  }, [currentUser, spaceId]);

  useEffect(() => {
    fetchPageTree();
  }, [fetchPageTree]);

  if (!spaceId) {
    return (
      <div className="wiki-sidebar">
        <h3 className="sidebar-title">No Space Selected</h3>
        <p>Please select a space from the <NavLink to="/wiki">homepage</NavLink>.</p>
      </div>
    );
  }

  return (
    <div className="wiki-sidebar">
      <h3 className="sidebar-title">
        {spaceName || 'Space'}
        <FontAwesomeIcon
          icon={faSyncAlt}
          className={`refresh-icon ${loading ? 'fa-spin' : ''}`}
          onClick={fetchPageTree}
          title="Refresh Pages"
        />
      </h3>
      <ul className="sidebar-page-list">
        {loading ? (
          <li>Loading pages...</li>
        ) : pageTree.length > 0 ? (
          pageTree.map(node => (
            <PageTreeItem key={node.id} node={node} spaceId={spaceId} level={0} />
          ))
        ) : (
          <li>No pages found in this space.</li>
        )}
      </ul>
    </div>
  );
};

export default WikiSidebar;
